import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './MemberDashboard.css';


const MemberDashboard = () => {
  const { user, member, wallet, authenticatedFetch, refreshUserData } = useAuth();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchMemberData();
  }, []);

  const fetchMemberData = async () => {
    try {
      setLoading(true);
      setError('');

      // Refresh user data to get latest wallet info
      await refreshUserData();

      // Fetch tournaments (this will be expanded later)
      const tournamentsResponse = await authenticatedFetch('/api/tournaments');
      if (tournamentsResponse.ok) {
        const tournamentsData = await tournamentsResponse.json();
        setTournaments(tournamentsData.tournaments || []);
      }

    } catch (err) {
      console.error('Error fetching member data:', err);
      setError('Failed to load member data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="member-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (!member) {
    return (
      <div className="member-dashboard">
        <div className="error-container">
          <h2>Member Data Not Found</h2>
          <p>Unable to load your member information. Please contact the tournament director.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="member-dashboard">
      <div className="dashboard-header">
        <h1>Welcome back, {member.firstName}!</h1>
        <p>Member Dashboard</p>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="dashboard-grid">
        {/* Member Information Card */}
        <div className="dashboard-card member-info">
          <h2>Member Information</h2>
          <div className="info-grid">
            <div className="info-item">
              <label>Name:</label>
              <span>{member.fullName}</span>
            </div>
            <div className="info-item">
              <label>GHIN:</label>
              <span>{member.ghin}</span>
            </div>
            <div className="info-item">
              <label>Handicap Index:</label>
              <span>{member.handicapIndex}</span>
            </div>
            <div className="info-item">
              <label>Email:</label>
              <span>{member.email || 'Not provided'}</span>
            </div>
            <div className="info-item">
              <label>Membership Status:</label>
              <span className={`status ${member.membershipStatus}`}>
                {member.membershipStatus}
              </span>
            </div>
            <div className="info-item">
              <label>Member Since:</label>
              <span>{formatDate(member.memberSince)}</span>
            </div>
          </div>
        </div>

        {/* Wallet Information Card */}
        <div className="dashboard-card wallet-info">
          <h2>Wallet Information</h2>
          {wallet ? (
            <div className="wallet-details">
              <div className="balance-display">
                <span className="balance-label">Current Balance</span>
                <span className={`balance-amount ${wallet.balance >= 0 ? 'positive' : 'negative'}`}>
                  {formatCurrency(wallet.balance)}
                </span>
              </div>
              <div className="wallet-status">
                <span className={`status ${wallet.status}`}>
                  {wallet.status}
                </span>
              </div>
              {wallet.lastTransactionDate && (
                <div className="last-transaction">
                  <small>Last transaction: {formatDate(wallet.lastTransactionDate)}</small>
                </div>
              )}
              <button
                className="view-wallet-button"
                onClick={() => window.location.href = `/wallet/${wallet.id}`}
              >
                View Full Wallet
              </button>
            </div>
          ) : (
            <div className="no-wallet">
              <p>No wallet found. Contact the tournament director to set up your wallet.</p>
            </div>
          )}
        </div>

        {/* Ask Balboney Card */}
        <div className="dashboard-card balboney-card">
          <h2>Ask Balboney</h2>
          <div className="balboney-content">
            <div className="balboney-icon-wrapper">
              <span className="balboney-emoji">🏌️</span>
            </div>
            <p className="balboney-description">
              Got a rules question? Balboney is our AI assistant trained on USGA Rules of Golf,
              Balboa Park local rules, hole hazards, and course tips.
            </p>
            <div className="balboney-features-mini">
              <span className="feature-tag">📖 USGA Rules</span>
              <span className="feature-tag">📍 Local Rules</span>
              <span className="feature-tag">⛳ Course Tips</span>
            </div>
            <a
              className="balboney-chat-button"
              href="https://balboney.sandiego.golf"
              target="_blank"
              rel="noopener noreferrer"
            >
              Chat with Balboney →
            </a>
          </div>
        </div>

        {/* Tournament Assignments Card */}
        <div className="dashboard-card tournament-info">
          <h2>Tournament Assignments</h2>
          {tournaments.length > 0 ? (
            <div className="tournaments-list">
              {tournaments.slice(0, 3).map(tournament => (
                <div key={tournament._id} className="tournament-item">
                  <h4>{tournament.name}</h4>
                  <p>Date: {formatDate(tournament.date)}</p>
                  <p>Status: <span className={`status ${tournament.status}`}>{tournament.status}</span></p>
                </div>
              ))}
              {tournaments.length > 3 && (
                <p className="more-tournaments">
                  And {tournaments.length - 3} more tournament(s)
                </p>
              )}
            </div>
          ) : (
            <div className="no-tournaments">
              <p>No tournament assignments found.</p>
            </div>
          )}
        </div>

        {/* Preferences Card */}
        <div className="dashboard-card preferences-info">
          <h2>Playing Preferences</h2>
          <div className="preferences-grid">
            {member.defaultPreferences ? (
              <>
                <div className="preference-item">
                  <label>Time Preference:</label>
                  <span>{member.defaultPreferences.timePreference || 'No preference'}</span>
                </div>
                <div className="preference-item">
                  <label>Ride/Walk:</label>
                  <span>{member.defaultPreferences.rideOrWalkPreference || 'No preference'}</span>
                </div>
                {member.defaultPreferences.playingPartnerPreferences && 
                 member.defaultPreferences.playingPartnerPreferences.length > 0 && (
                  <div className="preference-item partner-prefs">
                    <label>Preferred Partners:</label>
                    <div className="partners-list">
                      {member.defaultPreferences.playingPartnerPreferences.map((partner, index) => (
                        <span key={index} className="partner-tag">
                          {partner.displayName || `${partner.firstName} ${partner.lastName}`}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p>No preferences set</p>
            )}
            <button 
              className="edit-preferences-button"
              onClick={() => window.location.href = `/member/${member.id}/preferences`}
            >
              Edit Preferences
            </button>
          </div>
        </div>

        {/* Pace of Play Card */}
        {member.paceOfPlay && (
          <div className="dashboard-card pace-info">
            <h2>Pace of Play</h2>
            <div className="pace-stats">
              <div className="pace-item">
                <label>Rating:</label>
                <span className={`pace-rating ${member.paceOfPlay.paceRating}`}>
                  {member.paceOfPlay.paceRating || 'Unknown'}
                </span>
              </div>
              <div className="pace-item">
                <label>Total Rounds:</label>
                <span>{member.paceOfPlay.totalRounds || 0}</span>
              </div>
              {member.paceOfPlay.averageElapsedTime && (
                <div className="pace-item">
                  <label>Average Time:</label>
                  <span>{Math.round(member.paceOfPlay.averageElapsedTime / 60 * 10) / 10} hours</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="dashboard-actions">
        <button 
          className="action-button primary"
          onClick={() => window.location.href = `/member/${member.id}`}
        >
          View Full Profile
        </button>
        <button 
          className="action-button secondary"
          onClick={fetchMemberData}
        >
          Refresh Data
        </button>
      </div>

    </div>
  );
};

export default MemberDashboard;